import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import DropdownPortal from "../util/DropdownPortal";
import VideoDepartmentEditModal from "./VideoDepartmentEditModal";
import VideoDepartmentUsersModal from "./VideoDepartmentUsersModal";

require("./assets/css/Video.css");

const VideoDepartmentsPage = () => {
    document.title = "Departamentos de Vídeo | Plantão Rápido";

    const buttonRefs = useRef({});
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            setLoading(true);

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedDepartment(null);
        setShowEditModal(true);
    };

    const handleEdit = (department) => {
        setSelectedDepartment(department);
        setShowEditModal(true);
    };

    const handleManageUsers = (department) => {
        setSelectedDepartment(department);
        setShowUsersModal(true);
    };

    const handleToggleStatus = async (department) => {
        const newStatus = department.status === 1 ? 0 : 1;
        const action = newStatus === 1 ? 'ativar' : 'desativar';

        const result = await Swal.fire({
            title: `${newStatus === 1 ? 'Ativar' : 'Desativar'} departamento?`,
            text: `Deseja ${action} o departamento "${department.name}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: newStatus === 1 ? '#28a745' : '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: `Sim, ${action}`,
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/departments/register`,
                {
                    id: department.id,
                    name: department.name,
                    status: newStatus
                },
                true
            );

            if (response.code === 200) {
                Swal.fire('Sucesso', `Departamento ${action === 'ativar' ? 'ativado' : 'desativado'}!`, 'success');
                fetchDepartments();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Departamentos" pageTitle="Vídeo Chamada" />

                    {/* Stats */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value">{departments.length}</div>
                                <div className="stat-label">Total</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-success">
                                    {departments.filter(d => d.status === 1).length}
                                </div>
                                <div className="stat-label">Ativos</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-danger">
                                    {departments.filter(d => d.status === 0).length}
                                </div>
                                <div className="stat-label">Inativos</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Actions */}
                    <Row className="mb-3">
                        <Col md={12} className="text-end">
                            <button
                                className="btn btn-outline-primary me-2"
                                onClick={fetchDepartments}
                                disabled={loading}
                            >
                                <i className="fa fa-refresh me-2"></i> Atualizar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreate}
                            >
                                <i className="fa fa-plus me-2"></i> Novo Departamento
                            </button>
                        </Col>
                    </Row>

                    {/* Departments List */}
                    <div className="list-container">
                        {loading ? (
                            <Skeleton count={5} height={80} />
                        ) : departments.length > 0 ? (
                            departments.map((department) => (
                                <Col lg={12} key={department.id}>
                                    <Card className={`mb-2 p-2 shadow-sm ${department.status === 0 ? 'opacity-50' : ''}`}>
                                        <CardBody className="d-flex justify-content-between align-items-center p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3" style={{ backgroundColor: department.status === 1 ? '#28a745' : '#6c757d' }}>
                                                    <i className="fa fa-building"></i>
                                                </div>
                                                <div>
                                                    <h5 className="mb-1">
                                                        {department.name}
                                                        {department.status === 0 && (
                                                            <span className="badge bg-secondary ms-2">Inativo</span>
                                                        )}
                                                    </h5>
                                                    <p className="text-muted mb-0 small">
                                                        <i className="fa fa-users me-1"></i>
                                                        {department.users_count || department.user_count || 0} atendentes
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                <span className={`badge ${department.status === 1 ? 'bg-success' : 'bg-secondary'}`}>
                                                    {department.status === 1 ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </div>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    ref={(el) => (buttonRefs.current[department.id] = el)}
                                                    onClick={() => setDropdownOpenId(dropdownOpenId === department.id ? null : department.id)}
                                                >
                                                    <i className="fa fa-ellipsis-v"></i> Ações
                                                </button>

                                                <DropdownPortal
                                                    parentRef={{ current: buttonRefs.current[department.id] }}
                                                    show={dropdownOpenId === department.id}
                                                    onClose={() => setDropdownOpenId(null)}
                                                >
                                                    <ul className="dropdown-menu show">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleEdit(department);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-edit me-2"></i> Editar
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleManageUsers(department);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-users me-2"></i> Gerenciar Atendentes
                                                            </button>
                                                        </li>
                                                        <li><hr className="dropdown-divider" /></li>
                                                        <li>
                                                            <button
                                                                className={`dropdown-item ${department.status === 1 ? 'text-danger' : 'text-success'}`}
                                                                onClick={() => {
                                                                    handleToggleStatus(department);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className={`fa ${department.status === 1 ? 'fa-times' : 'fa-check'} me-2`}></i>
                                                                {department.status === 1 ? 'Desativar' : 'Ativar'}
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </DropdownPortal>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <div className="video-empty-state">
                                <i className="fa fa-building"></i>
                                <h5 className="text-muted">Nenhum departamento encontrado</h5>
                                <p className="text-muted">Crie departamentos para organizar os atendimentos por vídeo.</p>
                                <button className="btn btn-primary mt-3" onClick={handleCreate}>
                                    <i className="fa fa-plus me-2"></i> Criar Departamento
                                </button>
                            </div>
                        )}
                    </div>

                    {showEditModal && (
                        <VideoDepartmentEditModal
                            show={showEditModal}
                            onHide={() => {
                                setShowEditModal(false);
                                setSelectedDepartment(null);
                            }}
                            department={selectedDepartment}
                            onSave={fetchDepartments}
                        />
                    )}

                    {showUsersModal && (
                        <VideoDepartmentUsersModal
                            show={showUsersModal}
                            onHide={() => {
                                setShowUsersModal(false);
                                setSelectedDepartment(null);
                            }}
                            department={selectedDepartment}
                            onSave={fetchDepartments}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoDepartmentsPage;
